import styled from "styled-components";

import {
  CENTER_BUTTON_SIZE,
  COLLAPSE_TOGGLE_SIZE,
  LIGHT_TAB_COLORS,
  PROFILE_AVATAR_SIZE,
  TAB_BAR_COLLAPSED_WIDTH,
  TAB_BAR_CONTENT_HEIGHT,
  TAB_BAR_FLOAT_MARGIN_BOTTOM,
  TAB_BAR_FLOAT_MARGIN_H,
  TAB_BAR_FLOAT_RADIUS,
  TAB_BUTTON_SIZE,
} from "../../constants/tabBarLayout";

export const TabBarRoot = styled.aside`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: stretch;
  padding: 0 ${TAB_BAR_FLOAT_MARGIN_H}px
    calc(
      ${TAB_BAR_FLOAT_MARGIN_BOTTOM}px + max(8px, env(safe-area-inset-bottom))
    );
  box-sizing: border-box;
`;

export const TabBarShadow = styled.div<{ $collapsed: boolean }>`
  pointer-events: auto;
  width: ${({ $collapsed }) =>
    $collapsed ? `${TAB_BAR_COLLAPSED_WIDTH}px` : "100%"};
  max-width: 100%;
  height: ${TAB_BAR_CONTENT_HEIGHT}px;
  margin-left: ${({ $collapsed }) => ($collapsed ? "auto" : "0")};
  border-radius: ${TAB_BAR_FLOAT_RADIUS}px;
  background: ${({ theme }) =>
    theme.isDarkMode
      ? theme.colors.surfaceSecondary
      : LIGHT_TAB_COLORS.navbarBg};
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.12);
  transition: width 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
    margin-left 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const TabBarShell = styled.div<{ $collapsed: boolean }>`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: ${TAB_BAR_FLOAT_RADIUS}px;
  overflow: hidden;
  padding: 0 ${({ $collapsed }) => ($collapsed ? "6px" : "12px")};
`;

export const MainRailGroup = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-evenly;
`;

export const BottomRailGroup = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 0;
  height: 100%;
  padding-left: 4px;
`;

export const TabSlot = styled.div<{ $hidden?: boolean }>`
  flex: ${({ $hidden }) => ($hidden ? "0 0 0" : "1 1 0")};
  min-width: ${({ $hidden }) => ($hidden ? "0" : `${TAB_BUTTON_SIZE}px`)};
  max-width: ${({ $hidden }) => ($hidden ? "0" : "none")};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  transform: scale(${({ $hidden }) => ($hidden ? 0.4 : 1)});
  overflow: hidden;
  pointer-events: ${({ $hidden }) => ($hidden ? "none" : "auto")};
  transition: flex 0.3s ease, opacity 0.25s ease, transform 0.25s ease,
    min-width 0.3s ease, max-width 0.3s ease;
`;

export const TabButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${TAB_BUTTON_SIZE}px;
  height: ${TAB_BUTTON_SIZE}px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 50%;
  transition: transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1);

  &:hover {
    background: rgba(95, 51, 225, 0.08);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const CenterSlot = styled(TabSlot)``;

export const CenterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${CENTER_BUTTON_SIZE}px;
  height: ${CENTER_BUTTON_SIZE}px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(95, 51, 225, 0.35);
  transition: transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1);

  &:active {
    transform: scale(0.96);
  }
`;

export const CollapseToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${COLLAPSE_TOGGLE_SIZE}px;
  height: ${COLLAPSE_TOGGLE_SIZE}px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 50%;
  flex-shrink: 0;

  &:hover {
    background: rgba(95, 51, 225, 0.08);
  }
`;

export const ProfileTabButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: ${TAB_BUTTON_SIZE}px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 50%;
  flex-shrink: 0;
`;

export const ProfileAvatarRing = styled.div<{
  $focused?: boolean;
  $hasImage?: boolean;
}>`
  width: ${PROFILE_AVATAR_SIZE}px;
  height: ${PROFILE_AVATAR_SIZE}px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $hasImage, theme }) => {
    if ($hasImage) return "transparent";
    if (theme.isDarkMode) return theme.colors.surfaceTertiary;
    return LIGHT_TAB_COLORS.profileRingBg;
  }};
  border: ${({ $focused, theme }) =>
    $focused ? `2px solid ${theme.colors.primary}` : "none"};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
